import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2, ArrowRight, Home, ShoppingBag, ExternalLink, Crown, GraduationCap } from 'lucide-react';

import { API } from '@/config/api';

export default function PaymentSuccess() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  
  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    const pollPaymentStatus = async () => {
      try {
        const response = await fetch(`${API}/payments/status/${sessionId}`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to check payment status');
        }
        
        const data = await response.json();
        setPaymentData(data);
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          return true;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollCount >= 4) {
          setStatus('error');
          return true;
        }
        return false;
      }
    };
    
    const poll = async () => {
      const done = await pollPaymentStatus();
      if (!done && pollCount < 5) {
        setPollCount(prev => prev + 1);
        setTimeout(poll, 2000);
      }
    };
    
    poll();
  }, [sessionId, pollCount]);
  
  // Determine if this is a membership purchase and get the portal link
  const isMembershipPurchase = paymentData?.package_type === 'membership';
  const isDynastyBuilder = paymentData?.package_id?.includes('dynasty_builder');
  const portalLink = isDynastyBuilder 
    ? 'https://nrluniversity.com/portal?tier=dynasty' 
    : 'https://nrluniversity.com/portal?tier=architect';
  
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#0a0a0f] pt-20 flex items-center justify-center" data-testid="payment-loading">
        <Card className="bg-[#14120f] border-[#1c1814] max-w-md w-full mx-4">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="h-16 w-16 text-[#D4AF37] mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-bold text-white mb-2">Processing Payment...</h2>
            <p className="text-gray-400">Please wait while we confirm your payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-[#0a0a0f] pt-20 flex items-center justify-center" data-testid="payment-success">
        <Card className="bg-[#14120f] border-[#1c1814] max-w-lg w-full mx-4">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {isMembershipPurchase ? 'Welcome to the Family!' : 'Payment Successful!'}
            </h2>
            <p className="text-gray-400 mb-6">
              {isMembershipPurchase 
                ? `Your ${paymentData?.package_name || 'membership'} is now active. Check your email for access details.`
                : 'Thank you for your purchase. Your payment has been processed.'
              }
            </p>
            
            {paymentData && (
              <div className="bg-[#0a0a0f] rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-400">Amount</span>
                  <span className="text-white font-bold">${paymentData.amount?.toFixed(2)}</span>
                </div>
                {paymentData.package_name && (
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-400">Package</span>
                    <span className="text-[#D4AF37]">{paymentData.package_name}</span>
                  </div>
                )}
                {isMembershipPurchase && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Access Level</span>
                    <span className="text-emerald-400">
                      {isDynastyBuilder ? 'Full Access + Coaching' : 'Course Access'}
                    </span>
                  </div>
                )}
              </div>
            )}
            
            {/* NRL University Portal Access for Membership Purchases */}
            {isMembershipPurchase && (
              <div className="bg-gradient-to-r from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/30 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <GraduationCap className="h-5 w-5 text-[#D4AF37]" />
                  <span className="text-white font-semibold">Your NRL University Access</span>
                </div>
                <p className="text-gray-400 text-sm mb-4">
                  Access your courses, resources, and exclusive content at NRL University.
                </p>
                <a 
                  href={portalLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black font-semibold py-3 px-4 rounded-lg transition-colors"
                >
                  <Crown className="mr-2 h-5 w-5" />
                  Access NRL University Portal
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </div>
            )}
            
            <div className="flex flex-col gap-3">
              <Link to="/dashboard">
                <Button className={`w-full ${isMembershipPurchase ? 'bg-[#2d2722] hover:bg-[#3a3a4e] text-white' : 'bg-[#D4AF37] hover:bg-[#B8962E] text-black'}`}>
                  Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              {orderId && (
                <Link to={`/orders/${orderId}`}>
                  <Button variant="outline" className="w-full border-[#D4AF37] text-[#D4AF37]">
                    <ShoppingBag className="mr-2 h-4 w-4" /> View Order
                  </Button>
                </Link>
              )}
            </div>
            
            <p className="text-gray-500 text-xs mt-6">
              A confirmation email has been sent to your registered email address.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Error or expired state
  return (
    <div className="min-h-screen bg-[#0a0a0f] pt-20 flex items-center justify-center" data-testid="payment-error">
      <Card className="bg-[#14120f] border-[#1c1814] max-w-md w-full mx-4">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="h-12 w-12 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {status === 'expired' ? 'Payment Expired' : 'Payment Error'}
          </h2>
          <p className="text-gray-400 mb-6">
            {status === 'expired' 
              ? 'Your payment session has expired. Please try again.'
              : 'There was an issue processing your payment. Please try again or contact support.'
            }
          </p>
          
          <div className="flex flex-col gap-3">
            <Link to="/">
              <Button className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-black">
                <Home className="mr-2 h-4 w-4" /> Return Home
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
